// src/features/test-plans/api/usePlatforms.ts
// Standalone platforms hooks — imported by PlatformsPage, RecordExecutionPage, PlatformDialog
// All endpoints verified against openapi.json (testplan-service)
// All paths via API_CONFIG.ENDPOINTS

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api/client'
import { API_CONFIG } from '@/lib/config/api.config'
import type {
  Platform,
  PlatformsResponse,
  CreatePlatformRequest,
  UpdatePlatformRequest,
} from '@/types/testplan.types'

// Query keys
export const platformKeys = {
  all: ['platforms'] as const,
  lists: () => [...platformKeys.all, 'list'] as const,
  list: (filters?: Record<string, unknown>) =>
    [...platformKeys.lists(), filters] as const,
  details: () => [...platformKeys.all, 'detail'] as const,
  detail: (id: number) => [...platformKeys.details(), id] as const,
}

// ============================================================================
// Platforms — CRUD
// OpenAPI: POST/GET /api/platforms, GET/PUT/DELETE /api/platforms/{platform_id}
// ============================================================================

// List platforms with optional filters
// GET /api/platforms — params: project_id?, is_open? (default true), limit (default 100), offset (default 0)
// NOTE: OpenAPI query param is "project_id" (NOT "test_project_id" — that's the create body field)
export const usePlatforms = (params?: {
  project_id?: number
  is_open?: boolean
  limit?: number
  offset?: number
}) => {
  return useQuery({
    queryKey: platformKeys.list(params),
    queryFn: async () => {
      const qp = new URLSearchParams()
      if (params?.project_id)
        qp.append('project_id', params.project_id.toString())
      if (params?.is_open !== undefined)
        qp.append('is_open', params.is_open.toString())
      if (params?.limit) qp.append('limit', params.limit.toString())
      if (params?.offset) qp.append('offset', params.offset.toString())

      const url = qp.toString()
        ? `${API_CONFIG.ENDPOINTS.PLATFORMS.LIST}?${qp.toString()}`
        : API_CONFIG.ENDPOINTS.PLATFORMS.LIST
      const { data } = await apiClient.get<PlatformsResponse>(url)
      return data.platforms
    },
  })
}

// Get single platform by ID
// GET /api/platforms/{platform_id}
export const usePlatform = (id: number) => {
  return useQuery({
    queryKey: platformKeys.detail(id),
    queryFn: async () => {
      const { data } = await apiClient.get<Platform>(
        API_CONFIG.ENDPOINTS.PLATFORMS.GET(id.toString())
      )
      return data
    },
    enabled: !!id,
  })
}

// Create platform
// POST /api/platforms — body: { test_project_id (required), name (required), notes?, enable_on_design? (default false), enable_on_execution? (default true) }
// Returns 201
export const useCreatePlatform = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (platformData: CreatePlatformRequest) => {
      const { data } = await apiClient.post<Platform>(
        API_CONFIG.ENDPOINTS.PLATFORMS.CREATE,
        platformData
      )
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: platformKeys.lists() })
    },
  })
}

// Update platform
// PUT /api/platforms/{platform_id} — body: { name?, notes?, enable_on_design?, enable_on_execution?, is_open? }
export const useUpdatePlatform = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: number
      data: UpdatePlatformRequest
    }) => {
      const response = await apiClient.put<Platform>(
        API_CONFIG.ENDPOINTS.PLATFORMS.UPDATE(id.toString()),
        data
      )
      return response.data
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: platformKeys.lists() })
      queryClient.invalidateQueries({ queryKey: platformKeys.detail(data.id) })
    },
  })
}

// Delete platform (soft delete)
// DELETE /api/platforms/{platform_id} — Returns 204
export const useDeletePlatform = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: number) => {
      await apiClient.delete(
        API_CONFIG.ENDPOINTS.PLATFORMS.DELETE(id.toString())
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: platformKeys.lists() })
    },
  })
}
